/**
 * Mutations der Akte (Move/Reclassify/Ordner-CRUD/Delete). Move + Delete laufen optimistisch
 * über den Tree-Cache (sofortiges Feedback, Rollback bei Fehler); Reclassify + Ordner-Ops
 * invalidieren nur (Ziel-Ordner leitet das Backend ab). Fehler → kuratierte DE-Meldung (§3.6).
 */
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  toApiError,
  useApi,
  type PropertyDocumentKind,
  type PropertyFileResponse,
  type PropertyFileTreeNode,
} from '@/shared/api';
import { akteErrorMessage } from './akteErrorMessages';
import { PROPERTY_DOCUMENT_KIND_SHORT } from './akteKindLabels';
import { invalidatePropertyFiles } from './akteTreeCache';
import { moveFileInTree, removeFileFromTree } from './akteTreeOps';

function lastSegment(path: string): string {
  return path.split('/').filter(Boolean).pop() ?? path;
}

export function useAkteMutations(propertyId: string) {
  const api = useApi();
  const qc = useQueryClient();
  const treeKey = ['propertyFiles', 'tree', propertyId] as const;
  const invalidate = () => invalidatePropertyFiles(qc, propertyId);

  async function optimisticTree(
    transform: (t: PropertyFileTreeNode) => PropertyFileTreeNode,
    run: () => Promise<unknown>,
    onOk: () => void,
  ) {
    await qc.cancelQueries({ queryKey: treeKey });
    const prev = qc.getQueryData<PropertyFileTreeNode>(treeKey);
    if (prev) qc.setQueryData(treeKey, transform(prev));
    try {
      await run();
      onOk();
      invalidate();
    } catch (err) {
      if (prev) qc.setQueryData(treeKey, prev);
      toast.error(akteErrorMessage(await toApiError(err)));
    }
  }

  return {
    move: (file: PropertyFileResponse, targetFolder: string) =>
      optimisticTree(
        (t) => moveFileInTree(t, file.id, targetFolder),
        () => api.propertyFiles.move(propertyId, file.id, { targetFolder }),
        () => toast.success(`Nach ${lastSegment(targetFolder)} verschoben.`),
      ),

    deleteFile: (file: PropertyFileResponse) =>
      optimisticTree(
        (t) => removeFileFromTree(t, file.id),
        () => api.propertyFiles.delete(propertyId, file.id),
        () => toast.success('In den Papierkorb verschoben.'),
      ),

    reclassify: async (
      file: PropertyFileResponse,
      kind: PropertyDocumentKind,
      issuedOn?: string | null,
    ) => {
      try {
        await api.propertyFiles.reclassify(propertyId, file.id, { kind, issuedOn });
        toast.success(`Als ${PROPERTY_DOCUMENT_KIND_SHORT[kind]} neu einsortiert.`);
        invalidate();
      } catch (err) {
        toast.error(akteErrorMessage(await toApiError(err)));
        throw err;
      }
    },

    createFolder: async (path: string) => {
      try {
        await api.propertyFiles.createFolder(propertyId, { path });
        toast.success(`Ordner ${lastSegment(path)} angelegt.`);
        invalidate();
      } catch (err) {
        toast.error(akteErrorMessage(await toApiError(err)));
        throw err;
      }
    },

    renameFolder: async (path: string, newName: string) => {
      try {
        await api.propertyFiles.renameFolder(propertyId, { path, newName });
        toast.success(`Ordner umbenannt in ${newName}.`);
        invalidate();
      } catch (err) {
        toast.error(akteErrorMessage(await toApiError(err)));
        throw err;
      }
    },

    deleteFolder: (path: string) =>
      api.propertyFiles
        .deleteFolder(propertyId, path)
        .then(() => {
          toast.success(`Ordner ${lastSegment(path)} gelöscht.`);
          invalidate();
        })
        .catch(async (err: unknown) => {
          toast.error(akteErrorMessage(await toApiError(err)));
          throw err;
        }),
  };
}
